import { TrackingEventConstants } from '../../../../constants/tracking'
import CountUp from 'react-countup'
import { Waypoint } from 'react-waypoint'
import { useSelector } from 'react-redux'
import usePrev from '../../../../hooks/usePrev'
import { defineMessages, useIntl } from 'react-intl'
import { useState, useEffect } from 'react'
import { YourLoanSentence } from '../../../homeowners/YourLoanSentence/YourLoanSentence'
import { AdditionalInfo } from '../../../shared/AdditionalInfo'
import { useTrackingDebounceSetters } from '../../../../hooks/tracking'
import { DisclaimersButton, Format, Lock } from '../../../'
import { Box, Flex, Button, Icon, Text, Heading, Card } from '@homebotapp/hb-react-component-catalog'
import { ConfirmLoanDetailsInfoWrapper } from '../../PrincipalAndInterestModule/PaidSoFar/ConfirmLoanDetailsInfoWrapper'
import { useTrackingContext } from '../../../../providers/tracking'
import { HOMEOWNER_DIGEST_AVM_MODULE } from '../../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

export const MSG = defineMessages({
  header: {
    id: 'Homeowner.estimatedValue.netWorthHeadline',
    defaultMessage: 'That means the net worth of the home to you is'
  },
  explanation: {
    id: 'Homeowner.estimatedValue.equityExplanation',
    defaultMessage:
      'If you sold your home today, this is approximately how much you would put in your pocket (does not include selling fees, e.g., agent commissions, title cost, etc.)'
  },
  toggle: {
    id: 'Homeowner.estimatedValue.equityInfoToggleButton',
    defaultMessage: 'View Calculation'
  },
  toggleOff: {
    id: 'Homeowner.estimatedValue.equityInfoToggleOffButton',
    defaultMessage: 'Hide Calculation'
  },
  homeVal: {
    id: 'General.homeValue',
    defaultMessage: 'Home value'
  },
  loanBalance: {
    id: 'Homeowner.estimatedValue.loanBalance',
    defaultMessage: 'Loan balance'
  },
  yourNetWorth: {
    id: 'Homeowner.estimatedValue.yourNetWorth',
    defaultMessage: 'Your net worth'
  },
  forNetWorth: {
    id: 'Homeowner.estimatedValue.equityInfoToggleButtonContext',
    defaultMessage: 'for Net Worth'
  },
  a11yEquation: {
    id: 'Homeowner.estimatedValue.equation.a11y',
    defaultMessage:
      'Your home value of ${homeValue} minus your loan balance of ${outstandingPrincipal} which equals your net worth of ${estimatedValue}.'
  }
})

interface NetWorthSectionProps {
  homeValue: number
  estimatedValue: number
  outstandingPrincipal: number
  withCurrentHomeData: boolean
}

export const NetWorthSection = ({
  homeValue,
  estimatedValue,
  outstandingPrincipal,
  withCurrentHomeData
}: NetWorthSectionProps) => {
  const intl = useIntl()
  const { trackingClient } = useTrackingContext()
  const prevEstimatedValue = usePrev((estimatedValue ?? 0) || 0)
  const [countAnimating, setCountAnimating] = useState(false)
  const [showInfo, setShowInfo] = useState(false)

  const handleNetWorthToggle = () => {
    trackingClient?.netWorthExplored()
    setShowInfo(i => !i)
  }

  const [trackModuleViewed, clearDebounceForTrackModuleViewed] = useTrackingDebounceSetters(
    () =>
      trackingClient?.moduleViewed({
        module_name: TrackingEventConstants.NET_WORTH
      })
  )

  useEffect(() => {
    return () => {
      clearDebounceForTrackModuleViewed()
    }
  }, [clearDebounceForTrackModuleViewed])

  return (
    <>
      {withCurrentHomeData && <ConfirmLoanDetailsInfoWrapper />}
      <Heading mt={8} as='h3' fontWeight='light'>
        {intl.formatMessage(MSG.header)}
        <span className='screenReaderText'>${estimatedValue.toFixed(0)}</span>
      </Heading>
      <Text
        as='span'
        size='2xl'
        color='brand'
        fontWeight='light'
        data-animation-qa={countAnimating ? 'started' : 'done'}
      >
        <Lock enabled={!withCurrentHomeData}>
          <CountUp
            // @ts-ignore
            start={prevEstimatedValue}
            end={estimatedValue}
            separator=','
            prefix='$'
            onStart={() => setCountAnimating(true)}
            onEnd={() => setCountAnimating(false)}
          />
        </Lock>
      </Text>
      {Boolean(estimatedValue > 1000) && (
        <Text mb={8} mx='auto' size='2xs' maxW='20rem'>
          {intl.formatMessage(MSG.explanation)}
        </Text>
      )}
      {withCurrentHomeData && (
        <>
          <Waypoint onEnter={trackModuleViewed} bottomOffset='50%'>
            <div />
          </Waypoint>
          <Button
            size='sm'
            onClick={handleNetWorthToggle}
            aria-expanded={showInfo ? true : false}
            leftIcon={<Icon name='calculator' />}
            tracking={{
              guid: 'kXrydcq9VJJ1oVZ73EqPJt',
              ui_context: HOMEOWNER_DIGEST_AVM_MODULE,
              descriptor: showInfo ? MSG.toggleOff.defaultMessage : MSG.toggle.defaultMessage
            }}
          >
            {showInfo ? intl.formatMessage(MSG.toggleOff) : intl.formatMessage(MSG.toggle)}
            <span className='screenReaderText'> {intl.formatMessage(MSG.forNetWorth)}</span>
          </Button>
          <AdditionalInfo visible={showInfo} headingText=''>
            <Flex mb={8} gap={1} align='flex-end' justify='center'>
              <Box>
                <Text mb={0} size='2xs'>
                  {intl.formatMessage(MSG.homeVal)}
                </Text>
                <Card p={2}>
                  <Format.Currency value={homeValue} />
                </Card>
              </Box>
              <Icon h={3} mb={3} name='minus' />
              <Box>
                <Text mb={0} size='2xs'>
                  {intl.formatMessage(MSG.loanBalance)}
                </Text>
                <Card p={2}>
                  <Format.Currency value={Math.round(outstandingPrincipal)} />
                </Card>
              </Box>
              <Icon h={3} mb={3} name='equals' />
              <Box>
                <Text mb={0} size='2xs'>
                  {intl.formatMessage(MSG.yourNetWorth)}
                </Text>
                <Card p={2}>
                  <Format.Currency value={Math.round(estimatedValue)} />
                </Card>
              </Box>
            </Flex>
            {/* Screen Reader version of the equation */}
            <span className='screenReaderText'>
              {intl.formatMessage(MSG.a11yEquation, {
                homeValue: Math.round(homeValue),
                estimatedValue: Math.round(estimatedValue),
                outstandingPrincipal: Math.round(outstandingPrincipal)
              })}
            </span>
            <YourLoanSentence long />
            <DisclaimersButton />
          </AdditionalInfo>
        </>
      )}
    </>
  )
}
